import { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';

interface ValidationResult {
  id: number;
  guest_id: number;
  guest_name: string;
  passport_number: string;
  document_type: string;
  validation_status: string;
  validation_date: string;
  expiry_date: string;
  issues: string[];
  hotel_name: string;
}

function DocumentValidationPage() {
  const [validations, setValidations] = useState<ValidationResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchValidations();
  }, []);

  const fetchValidations = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      // Correct gateway path (not /security/documents/...)
      const response = await fetch(`${API_URL}/api/v1/documents/validations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.data || data.validations || []);
        setValidations(list);
      } else {
        setValidations([]);
        setError(`Impossible de charger les validations (HTTP ${response.status})`);
      }
    } catch (err) {
      console.error('Error fetching validations:', err);
      setValidations([]);
      setError('Service de validation des documents indisponible');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'valid':
      case 'valide':
        return 'text-green-600 bg-green-100';
      case 'expired':
      case 'expiré':
        return 'text-red-600 bg-red-100';
      case 'expiring_soon':
      case 'expire_bientôt':
        return 'text-yellow-600 bg-yellow-100';
      case 'invalid':
      case 'invalide':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'valid':
        return 'Valide';
      case 'expired':
        return 'Expiré';
      case 'expiring_soon':
        return 'Expire bientôt';
      case 'invalid':
        return 'Invalide';
      default:
        return status;
    }
  };

  const filteredValidations = validations.filter(validation => {
    if (filterStatus === 'all') return true;
    return validation.validation_status.toLowerCase() === filterStatus.toLowerCase();
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📄 Validation des Documents</h1>
          <p className="text-gray-600">Vérification des passeports, visas et documents d'identité</p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {error}
          </div>
        )}

        {/* Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">
              Statut:
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous</option>
              <option value="valid">Valide</option>
              <option value="expiring_soon">Expire bientôt</option>
              <option value="expired">Expiré</option>
              <option value="invalid">Invalide</option>
            </select>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Documents</div>
            <div className="text-2xl font-bold text-gray-900">{validations.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Valides</div>
            <div className="text-2xl font-bold text-green-600">
              {validations.filter(v => (v.validation_status || '').toLowerCase() === 'valid').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Expire bientôt</div>
            <div className="text-2xl font-bold text-yellow-600">
              {validations.filter(v => (v.validation_status || '').toLowerCase() === 'expiring_soon').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Problèmes</div>
            <div className="text-2xl font-bold text-red-600">
              {validations.filter(v => {
                const s = (v.validation_status || '').toLowerCase();
                return s === 'expired' || s === 'invalid';
              }).length}
            </div>
          </div>
        </div>

        {/* Validations Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hôtel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date validation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Problèmes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredValidations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Aucune validation trouvée
                  </td>
                </tr>
              ) : (
                filteredValidations.map((validation) => (
                  <tr key={validation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {validation.guest_name}
                      </div>
                      <div className="text-sm text-gray-500">{validation.passport_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {validation.document_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {validation.hotel_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(validation.validation_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {validation.expiry_date ? new Date(validation.expiry_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(validation.validation_status)}`}>
                        {getStatusLabel(validation.validation_status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {validation.issues && validation.issues.length > 0 ? (
                        <ul className="list-disc list-inside">
                          {validation.issues.map((issue, idx) => (
                            <li key={idx} className="text-red-600">{issue}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-green-600">Aucun</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DocumentValidationPage;